import { type ChangeEvent, useEffect, useState } from 'react';

import { Checkbox, FormControlLabel, InputLabel, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useContainer } from 'unstated-next';

import { isUuidValid } from '../../../utils/uuid';
import AdminSettingsContainer from '../AdminSettingsContainer';
import { StyledFormGroup, StyledFormGroupContainer } from '../styles';

const validateTenantIds = (tenantIds?: string[]) => {
    if (!tenantIds || tenantIds.length === 0) {
        return true;
    }

    return !tenantIds.find((tenantId) => !isUuidValid(tenantId));
};

export const TeamsAdminSettings = () => {
    const { adminSettings, setAdminSettings, setSavingDisabled, school } = useContainer(AdminSettingsContainer);
    const { classForTeamsSettings, featureSettings } = adminSettings;

    const { t } = useTranslation('admin_settings');

    const [isTenantIdValid, setIsTenantIdValid] = useState(
        validateTenantIds(adminSettings.classForTeamsSettings.azureTenantIds)
    );

    const handleClassForTeamsSettingsChange = (event: ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;

        if (name === 'azureTenantIds') {
            if (value === '') {
                setIsTenantIdValid(true);

                classForTeamsSettings.azureTenantIds = [];
                setAdminSettings({ ...adminSettings });

                return;
            }

            classForTeamsSettings.azureTenantIds = value.split(',').map((id) => id.trim());
            setIsTenantIdValid(validateTenantIds(classForTeamsSettings.azureTenantIds));
        } else {
            classForTeamsSettings[name] = value;
        }

        setAdminSettings({ ...adminSettings });
    };

    const handleUpdateOptionsCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        featureSettings.options[event.target.name] = event.target.checked;
        setAdminSettings({ ...adminSettings });
    };

    useEffect(() => {
        setSavingDisabled(!isTenantIdValid);
    }, [isTenantIdValid, setSavingDisabled]);

    return (
        <StyledFormGroupContainer>
            <StyledFormGroup>
                <InputLabel htmlFor="azureTenantIds">{t('auth.azure-tenant-id-label')}</InputLabel>
                <TextField
                    value={classForTeamsSettings.azureTenantIds?.join(', ') ?? ''}
                    onChange={handleClassForTeamsSettingsChange}
                    id="azureTenantIds"
                    name="azureTenantIds"
                    error={!isTenantIdValid}
                    helperText={
                        isTenantIdValid
                            ? t('auth.azure-tenant-id-helper-text')
                            : t('auth.azure-tenant-id-helper-text-invalid')
                    }
                    size="small"
                />
            </StyledFormGroup>

            <FormControlLabel
                control={
                    <Checkbox
                        checked={featureSettings.options.autoProvisionNewUsersMicrosoft}
                        onChange={handleUpdateOptionsCheckboxChange}
                        id="autoProvisionNewUsersMicrosoft"
                        name="autoProvisionNewUsersMicrosoft"
                    />
                }
                label={t('auth.allow-account-provisioning')}
            />
        </StyledFormGroupContainer>
    );
};
